import {
  CloseOutline18Icon,
  HistoryOutline24Icon,
} from "@vygruppen/spor-icon-react";
import {
  getLocalStorageTodoFilterSettings,
  setLocalStorageTodoFilterSettings,
} from "api/dropsLocalStorage";
import {
  FILTER_STATUS_LIST,
  isDefault,
  TodoFilterState,
  TodoFilterStatus,
  VEHICLE_SET_TYPES,
} from "features/TodoOploggSidebar/Todo/todoFilter/useTodoFilterState";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Chip,
  DropsButton,
  FilterChip,
  Flex,
  HStack,
  MenuCheckboxItem,
  MenuItemGroup,
  Text,
} from "shared/components/Spor";
import {
  getUpdatedRoles,
  isValidUserRole,
  TODO_ROLES,
  UserRole,
} from "shared/types/roles";
import { useDropsRole } from "stores/useDropsRole";
import { useTheme } from "styled-components";

interface TodoFilterProps {
  todoFilterState: TodoFilterState;
  setTodoFilterState: (state: TodoFilterState) => void;
  clearTodoFilterState: () => void;
  toggleRoles: (role: UserRole) => void;
  toggleVehicleSetTypes: (vehicleSetType: string) => void;
  toggleTodoStatus: (todoStatus: TodoFilterStatus) => void;
}

const TodoFilter = ({
  todoFilterState,
  setTodoFilterState,
  clearTodoFilterState,
  toggleRoles,
  toggleVehicleSetTypes,
  toggleTodoStatus,
}: TodoFilterProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { role: currentRole } = useDropsRole();

  const getVehicleSetTypeLabel = (text: string): string =>
    text === "Lok og vogn" ? t("Lok og vogn") : text;

  const getTodoStatusLabel = (todoStatus: TodoFilterStatus) => {
    switch (todoStatus) {
      case "completed":
        return t("Vis fullførte oppgaver");
      case "incomplete":
      case "futureAlarms":
        return "";
    }
  };

  const allRoles = TODO_ROLES.reduce<string[]>(
    (selectedRoles, role) => getUpdatedRoles(selectedRoles, role.value),
    [],
  ).filter(isValidUserRole);
  const allRolesSelected =
    todoFilterState.roles.length !== 0 &&
    allRoles.every((role) => todoFilterState.roles.includes(role));

  useEffect(() => {
    const savedFilterState = getLocalStorageTodoFilterSettings();
    if (
      savedFilterState &&
      JSON.stringify(savedFilterState) !== JSON.stringify(todoFilterState)
    ) {
      setTodoFilterState(savedFilterState);
    }
  }, []);

  useEffect(() => {
    setLocalStorageTodoFilterSettings(todoFilterState);
  }, [todoFilterState]);

  return (
    <Flex
      flexDirection="column"
      gap={2}
      backgroundColor={theme.surfaceDefault}
      maxWidth="331px"
      maxHeight="min(850px, calc(100vh - 240px))"
      overflowY="auto"
      px={0.5}
    >
      <HStack width="100%" justifyContent="space-between">
        <Text variant="sm" fontWeight="bold">
          {t("Filtrer på:")}
        </Text>
        {!isDefault(todoFilterState, currentRole) && (
          <DropsButton
            variant="ghost"
            size="xs"
            fontWeight="normal"
            onClick={clearTodoFilterState}
            rightIcon={<CloseOutline18Icon />}
          >
            {t("Nullstill filtre")}
          </DropsButton>
        )}
      </HStack>

      <Text variant="xs" fontWeight="bold">
        {t("Rolle")}
      </Text>
      <MenuItemGroup>
        <MenuCheckboxItem
          value="ALL"
          cursor="pointer"
          checked={todoFilterState.roles.length !== 0 && allRolesSelected}
          onCheckedChange={() =>
            setTodoFilterState({
              ...todoFilterState,
              roles: allRolesSelected ? [] : allRoles,
            })
          }
        >
          {t("Marker alle")}
        </MenuCheckboxItem>
        {TODO_ROLES.map((role) => (
          <MenuCheckboxItem
            key={role.value}
            value={role.value}
            cursor="pointer"
            checked={todoFilterState.roles.includes(role.value)}
            onCheckedChange={() => toggleRoles(role.value)}
          >
            {role.text}
          </MenuCheckboxItem>
        ))}
      </MenuItemGroup>

      <Text variant="xs" fontWeight="bold">
        {t("Togtype")}
      </Text>
      <Flex w="100%" flexWrap="wrap" gap="6px" pl={1}>
        {VEHICLE_SET_TYPES.map(({ text, value }) => (
          <FilterChip
            key={text}
            value={value}
            checked={todoFilterState.vehicleSetTypes.includes(value)}
            onCheckedChange={() => toggleVehicleSetTypes(value)}
            width="fit-content"
            whiteSpace="nowrap"
          >
            {getVehicleSetTypeLabel(text)}
          </FilterChip>
        ))}
      </Flex>

      <Text variant="xs" fontWeight="bold">
        {t("Status")}
      </Text>
      <MenuItemGroup p={1}>
        {FILTER_STATUS_LIST.map((value) => (
          <Chip
            key={value}
            value={value}
            width="fit-content"
            icon={<HistoryOutline24Icon />}
            checked={todoFilterState.todoStatus.includes(value)}
            onCheckedChange={() => toggleTodoStatus(value)}
          >
            {getTodoStatusLabel(value)}
          </Chip>
        ))}
      </MenuItemGroup>
    </Flex>
  );
};

export default TodoFilter;
